import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Sparkles, Clock, DollarSign, CheckCircle2, Circle,
  Calendar, User, Image, ChevronRight, Loader2
} from 'lucide-react';
import api from '../lib/api';
import useWin98Scope from '../hooks/useWin98Scope';

const SharedCosplayPage = () => {
  useWin98Scope();
  const { shareToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  useEffect(() => {
    const fetchSharedCosplay = async () => {
      try {
        const response = await api.get(`/share/${shareToken}`);
        setData(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'This cosplay is no longer shared or does not exist');
      } finally {
        setLoading(false);
      }
    };

    fetchSharedCosplay();
  }, [shareToken]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="glass-card p-8 text-center max-w-md">
          <p className="text-4xl mb-3">⚠</p>
          <h1 className="text-2xl font-heading font-bold mb-2">Not Found</h1>
          <p className="mb-6">{error}</p>
          <Link
            to="/"
            className="win-btn inline-flex items-center gap-2"
          >
            Go to KyberCosplay <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  const { cosplay, owner_name, task_categories, photos, recent_time_logs } = data;

  const totalTasks = task_categories.reduce((sum, cat) => sum + cat.total_tasks, 0);
  const completedTasks = task_categories.reduce((sum, cat) => sum + cat.completed_tasks, 0);
  const progressPercent = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  // Scoped win98 status classes (index.css) — face chip + border, no colored pill.
  const statusClass = {
    planning: 'status-planning',
    in_progress: 'status-in_progress',
    completed: 'status-completed'
  };

  return (
    <div className="min-h-screen bg-slate-950 relative overflow-hidden">
      <div className="relative z-10 max-w-6xl mx-auto p-4 md:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <div className="w-10 h-10 win-raised bg-[var(--win-titlebar-start)] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-heading font-bold">KyberCosplay</span>
          </Link>
          <div className="flex items-center gap-2 text-sm">
            <User className="w-4 h-4" />
            <span>by {owner_name}</span>
          </div>
        </div>

        {/* Cover Image & Title */}
        <div className="glass-card overflow-hidden mb-8">
          {cosplay.cover_image ? (
            <div className="aspect-video md:aspect-[3/1] relative">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL || ''}${cosplay.cover_image}`}
                alt={cosplay.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/50" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <span className={`inline-block px-3 py-1 text-xs font-medium mb-3 ${statusClass[cosplay.status]}`}>
                  {cosplay.status.replace('_', ' ').toUpperCase()}
                </span>
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-2">{cosplay.title}</h1>
                <p className="text-xl text-white">{cosplay.character_name} {cosplay.series_name && `• ${cosplay.series_name}`}</p>
              </div>
            </div>
          ) : (
            <div className="p-6">
              <span className={`inline-block px-3 py-1 text-xs font-medium mb-3 ${statusClass[cosplay.status]}`}>
                {cosplay.status.replace('_', ' ').toUpperCase()}
              </span>
              <h1 className="text-3xl md:text-4xl font-heading font-bold mb-2">{cosplay.title}</h1>
              <p className="text-xl">{cosplay.character_name} {cosplay.series_name && `• ${cosplay.series_name}`}</p>
            </div>
          )}
        </div>

        {/* Build Stats */}
        <fieldset className="win-groupbox mb-8">
          <legend className="win-groupbox-label">Build Stats</legend>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="w-5 h-5" />
                <span className="text-sm">Progress</span>
              </div>
              <p className="text-2xl font-mono font-bold">{progressPercent}%</p>
              <p className="text-xs">{completedTasks}/{totalTasks} tasks</p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-5 h-5" />
                <span className="text-sm">Time</span>
              </div>
              <p className="text-2xl font-mono font-bold">{cosplay.total_time_hours.toFixed(1)}h</p>
              <p className="text-xs">logged</p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-5 h-5" />
                <span className="text-sm">Spent</span>
              </div>
              <p className="text-2xl font-mono font-bold">${cosplay.total_spent.toFixed(0)}</p>
              <p className="text-xs">of ${cosplay.budget} budget</p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="w-5 h-5" />
                <span className="text-sm">Target</span>
              </div>
              <p className="text-lg font-medium truncate">{cosplay.target_event || 'Not set'}</p>
              <p className="text-xs">{cosplay.target_date ? new Date(cosplay.target_date).toLocaleDateString() : 'No date'}</p>
            </div>
          </div>
        </fieldset>

        {/* Description */}
        {cosplay.description && (
          <div className="glass-card p-6 mb-8">
            <h2 className="text-lg font-heading font-semibold mb-3">About this Cosplay</h2>
            <p className="whitespace-pre-wrap">{cosplay.description}</p>
          </div>
        )}

        {/* Two Column Layout */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Tasks */}
          <div className="glass-card p-6">
            <h2 className="text-lg font-heading font-semibold mb-4 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              Tasks
            </h2>
            {task_categories.length === 0 ? (
              <p className="text-sm">No tasks added yet</p>
            ) : (
              <div className="space-y-6">
                {task_categories.map((category) => (
                  <fieldset key={category.id} className="win-groupbox">
                    <legend className="win-groupbox-label">{category.name}</legend>
                    <div className="flex items-center justify-end mb-2">
                      <span className="text-xs">
                        {category.completed_tasks}/{category.total_tasks}
                      </span>
                    </div>
                    {/* Progress bar — sunken white trough, navy fill */}
                    <div className="h-2 bg-[var(--win-window-bg)] border border-[var(--win-shadow)] mb-3 overflow-hidden">
                      <div
                        className="h-full bg-[var(--win-titlebar-start)]"
                        style={{ width: `${category.total_tasks > 0 ? (category.completed_tasks / category.total_tasks) * 100 : 0}%` }}
                      />
                    </div>
                    <div className="space-y-2">
                      {category.tasks.map((task) => (
                        <div key={task.id} className="flex items-center gap-2 text-sm">
                          {task.completed ? (
                            <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                          ) : (
                            <Circle className="w-4 h-4 flex-shrink-0" />
                          )}
                          <span className={task.completed ? 'line-through' : ''}>
                            {task.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </fieldset>
                ))}
              </div>
            )}
          </div>

          {/* Photos */}
          <div className="glass-card p-6">
            <h2 className="text-lg font-heading font-semibold mb-4 flex items-center gap-2">
              <Image className="w-5 h-5" />
              Photos ({photos.length})
            </h2>
            {photos.length === 0 ? (
              <p className="text-sm">No photos added yet</p>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {photos.map((photo) => (
                  <button
                    key={photo.id}
                    onClick={() => setSelectedPhoto(photo)}
                    className="aspect-square overflow-hidden win-raised p-0"
                  >
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL || ''}${photo.url}`}
                      alt={photo.caption || 'Photo'}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent Time Logs */}
        {recent_time_logs.length > 0 && (
          <div className="glass-card p-6 mt-8">
            <h2 className="text-lg font-heading font-semibold mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Recent Work Sessions
            </h2>
            <div className="space-y-3">
              {recent_time_logs.map((log) => (
                <div key={log.id} className="flex items-center justify-between py-2 border-b border-[var(--win-shadow)] last:border-0">
                  <div>
                    <p>{log.description || 'Work session'}</p>
                    <p className="text-xs">{new Date(log.work_date).toLocaleDateString()}</p>
                  </div>
                  <span className="font-mono">{log.hours}h</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-12 pb-8">
          <p className="text-sm mb-4">
            Track your own cosplay projects with KyberCosplay
          </p>
          <Link
            to="/register"
            className="win-btn win-btn-accent inline-flex items-center gap-2 px-6 py-3 font-semibold"
          >
            Create Your Account <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Photo Modal — flat scrim behind a win-window frame (design doc Modal pattern) */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <div
            className="win-window max-w-4xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="win-titlebar">
              <span className="truncate">{selectedPhoto.caption || 'Photo'}</span>
              <button
                className="win-btn win-btn-icon"
                aria-label="Close"
                onClick={() => setSelectedPhoto(null)}
              >
                ✕
              </button>
            </div>
            <div className="p-2 overflow-auto">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL || ''}${selectedPhoto.url}`}
                alt={selectedPhoto.caption || 'Photo'}
                className="max-w-full max-h-[80vh] object-contain block mx-auto"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SharedCosplayPage;
